import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Scan,
  FileText,
  MapPin,
  MessageSquare,
  Building2,
  FolderOpen,
  Rocket,
  ShieldCheck,
} from "lucide-react";

const PILLARS = [
  {
    id: "prescribe",
    icon: Scan,
    title: "Prescribe exactly what you want, tooth by tooth",
    pitch:
      "No more vague case notes. Build your Rx the way you think, tooth by tooth, with restorations, materials, and shades dialed in.",
    details: [
      "Interactive tooth chart, set status and restoration per tooth, not per case",
      "Advanced mode for detailed per-tooth restoration setup",
      "Implant and abutment libraries built in",
      "Shade picker for teeth and gum shades",
      "Material, grade, retention, and impression type selection",
      "Add-ons attached to specific teeth",
      "3D tooth visualization",
    ],
    mock: "chart",
  },
  {
    id: "create",
    icon: FileText,
    title: "Create an Rx in under a minute",
    pitch:
      "A digital Rx replaces paper, faxing, and phone call-ins. Handle multi-product and multi-stage cases on a single Rx.",
    details: [
      "Digital Rx, no paper, no faxing, no calling it in",
      "Multi-product and multi-stage cases on a single Rx",
      "Attach photos, scans, and STL files directly to the case",
      "Edit an Rx after creating it",
      "Print a paper copy whenever you still need one",
      "Set and change delivery dates",
    ],
    mock: "slip",
    highlight: "Only two other companies on the market let you do multi-product and multi-stage cases on one Rx.",
  },
  {
    id: "labs",
    icon: Building2,
    title: "All your labs, in one place",
    pitch:
      "Send every case to every lab you work with, all from one dashboard. Looking for a new lab? Search by name, connect, and send your first case in minutes.",
    details: [
      "Every lab you work with, in one dashboard",
      "No separate logins or portals per lab",
      "Search for a new lab by name and connect directly",
      "Send your first case to a newly connected lab in minutes",
    ],
    mock: "labs",
  },
  {
    id: "track",
    icon: MapPin,
    title: "Know where every case is, without calling",
    pitch:
      "Open your live case list to see status and due date right away. Then open any case to see the full Rx.",
    details: [
      "Live case list with current status and due date on every case",
      "Filter by status, doctor, location, stage, or whether attachments exist",
      "Search across your cases",
      "Full case view, open any case and see the complete Rx as submitted",
    ],
    mock: "list",
  },
  {
    id: "communicate",
    icon: MessageSquare,
    title: "Stop playing phone tag with the lab",
    pitch:
      "Notes, call logs, and notifications all live on the case, so nothing falls between the front desk and the lab.",
    details: [
      "Case notes, written, timestamped, and attached to the case",
      "Call logs so nothing gets lost between the front desk and the lab",
      "In-app notifications",
      "Add a note to a case after it's submitted. Your lab is notified automatically.",
    ],
    mock: "notes",
  },
  {
    id: "studio",
    icon: FolderOpen,
    title: "My Studio, every photo and file in one place",
    pitch:
      "Open My Studio to see your full case history: every photo, video, and STL file, all together.",
    details: [
      "Full history of pictures, videos, and STL files across every case",
      "Browse by patient, case, or date",
      "No digging through individual cases to find an old file",
    ],
    mock: "studio",
  },
  {
    id: "practice",
    icon: Building2,
    title: "Built for how practices actually run",
    pitch:
      "One login across locations, clear roles, and cases assigned to the right doctor and lab, with a dashboard your team configures.",
    details: [
      "One login across multiple locations, with a location switcher",
      "Separate roles for office admin, doctor, doctor-admin, and staff",
      "Assign each case to the right doctor and the right lab",
      "Configurable dashboard: set it up the way your practice thinks",
    ],
    mock: "practice",
  },
  {
    id: "onboarding",
    icon: Rocket,
    title: "Up and running fast",
    pitch:
      "Guided setup gets your hours, products, and team in place. Then invite your lab and start sending cases.",
    details: [
      "Guided onboarding: business hours, products, grades, attachments",
      "Invite your whole team during setup",
      "Invite your lab",
    ],
    mock: "onboard",
  },
  {
    id: "security",
    icon: ShieldCheck,
    title: "Safe and accountable",
    pitch:
      "HIPAA-ready controls, a full history log, and permissions that match how your practice shares access.",
    details: [
      "HIPAA compliance built in",
      "History log, a record of who did what and when",
      "Granular permission controls per user",
    ],
    mock: "secure",
  },
];

const OUTCOMES = [
  { label: "Prescribe", value: "Tooth by tooth" },
  { label: "Create", value: "Rx in under a minute" },
  { label: "Track", value: "Live status" },
  { label: "Collaborate", value: "Notes on the case" },
];

export default function Doctors() {
  const [active, setActive] = useState(PILLARS[0].id);

  useEffect(() => {
    const nodes = PILLARS.map((p) => document.getElementById(p.id)).filter(Boolean);
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -55% 0px", threshold: [0.15, 0.35, 0.55] }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <PageHero
        chapter="§ 04"
        eyebrow="For doctors"
        title={"Create a precise Rx. Track it without calling."}
        description="Rxn3D gives dentists and office teams a digital Rx, tooth-level detail, live case status, and communication tied to the case, so the lab gets exactly what you meant the first time."
        testId="doctors-hero"
      >
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/request-demo"
            data-testid="doctors-hero-cta"
            className="group inline-flex items-center gap-1.5 text-[13px] font-semibold pl-4 pr-3 py-2 rounded-full bg-foreground text-background hover:opacity-90 transition-opacity"
          >
            Request a doctor demo
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            type="button"
            onClick={() => scrollTo("prescribe")}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium hairline rounded-full px-4 py-2 hover:bg-secondary transition-colors"
          >
            Explore doctor features
          </button>
        </div>
      </PageHero>

      <section data-testid="doctors-outcomes" className="pb-10 lg:pb-14">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px rounded-3xl hairline overflow-hidden bg-border/60">
            {OUTCOMES.map((o, i) => (
              <motion.div
                key={o.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-background px-5 py-6 sm:px-6 sm:py-7"
              >
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                  {o.label}
                </div>
                <div className="mt-2 font-heading text-lg sm:text-xl font-semibold tracking-tight">
                  {o.value}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="doctors-features" className="relative pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-10 lg:items-start">
            <aside className="hidden lg:block lg:col-span-3 sticky top-24 self-start">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-4">
                Doctor features
              </div>
              <nav className="space-y-1" aria-label="Doctor feature sections">
                {PILLARS.map((p, i) => {
                  const isActive = active === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => scrollTo(p.id)}
                      data-testid={`doctors-nav-${p.id}`}
                      className={`w-full text-left rounded-xl px-3 py-2.5 transition-colors ${
                        isActive ? "bg-secondary/80" : "hover:bg-secondary/40"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`font-mono text-[10px] tracking-[0.18em] mt-0.5 ${
                            isActive ? "text-brand-gradient font-semibold" : "text-muted-foreground"
                          }`}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span
                          className={`text-[13px] leading-snug ${
                            isActive ? "text-foreground font-medium" : "text-muted-foreground"
                          }`}
                        >
                          {p.title.split(",")[0]}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </aside>

            <div className="lg:col-span-9 space-y-8 lg:space-y-12">
              {/* Mobile jump chips */}
              <div className="lg:hidden -mx-5 px-5 overflow-x-auto pb-2">
                <div className="flex gap-2 min-w-max">
                  {PILLARS.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => scrollTo(p.id)}
                      className={`shrink-0 rounded-full hairline px-3.5 py-1.5 text-[12px] font-medium transition-colors ${
                        active === p.id ? "bg-foreground text-background" : "hover:bg-secondary"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")} · {shortLabel(p.id)}
                    </button>
                  ))}
                </div>
              </div>

              {PILLARS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.article
                    key={p.id}
                    id={p.id}
                    data-testid={`doctors-feature-${p.id}`}
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="scroll-mt-28 rounded-3xl hairline glass p-6 sm:p-8 lg:p-10 relative overflow-hidden"
                  >
                    <div
                      aria-hidden
                      className="absolute -top-24 -right-16 h-56 w-56 rounded-full bg-brand-radial blur-3xl opacity-50 pointer-events-none"
                    />

                    <div className="relative grid grid-cols-1 xl:grid-cols-12 gap-8 xl:gap-10 items-start">
                      <div className="xl:col-span-6">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 rounded-xl hairline inline-flex items-center justify-center">
                            <Icon className="h-4 w-4 text-brand-plum" />
                          </div>
                          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                            Feature {String(i + 1).padStart(2, "0")}
                          </span>
                        </div>

                        <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-[2rem] font-semibold tracking-[-0.02em] leading-[1.12]">
                          {p.title}
                        </h2>
                        <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {p.pitch}
                        </p>

                        {p.highlight && (
                          <p className="mt-4 text-[13px] leading-relaxed rounded-2xl hairline bg-secondary/40 px-4 py-3">
                            {p.highlight}
                          </p>
                        )}

                        <ul className="mt-6 space-y-2.5">
                          {p.details.map((d) => (
                            <li key={d} className="flex items-start gap-2.5 text-sm leading-relaxed">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gradient" />
                              <span>{d}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="xl:col-span-6">
                        <DoctorMock type={p.mock} index={i} />
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </>
  );
}

function shortLabel(id) {
  const map = {
    prescribe: "Prescribe",
    create: "Create",
    labs: "Labs",
    track: "Track",
    communicate: "Communicate",
    studio: "My Studio",
    practice: "Practice",
    onboarding: "Onboard",
    security: "Security",
  };
  return map[id] || id;
}

function DoctorMock({ type, index }) {
  return (
    <div className="relative rounded-2xl hairline glass grain overflow-hidden">
      <svg viewBox="0 0 640 400" className="w-full h-auto" aria-hidden>
        <defs>
          <linearGradient id={`dm-${index}`} x1="0" x2="1">
            <stop offset="0%" stopColor="#2AA6DE" />
            <stop offset="55%" stopColor="#82298D" />
            <stop offset="100%" stopColor="#C9539F" />
          </linearGradient>
        </defs>

        {/* chrome */}
        <rect x="14" y="14" width="612" height="36" rx="10" className="fill-black/[0.03] dark:fill-white/[0.05]" />
        <circle cx="34" cy="32" r="4" className="fill-foreground/25" />
        <circle cx="48" cy="32" r="4" className="fill-foreground/25" />
        <circle cx="62" cy="32" r="4" className="fill-foreground/25" />
        <rect x="90" y="26" width="160" height="12" rx="6" className="fill-foreground/20" />

        {type === "chart" && <ChartMock fill={`url(#dm-${index})`} />}
        {type === "slip" && <SlipMock fill={`url(#dm-${index})`} />}
        {type === "labs" && <LabsMock fill={`url(#dm-${index})`} />}
        {type === "list" && <ListMock fill={`url(#dm-${index})`} />}
        {type === "notes" && <NotesMock fill={`url(#dm-${index})`} />}
        {type === "studio" && <StudioMock fill={`url(#dm-${index})`} />}
        {type === "practice" && <PracticeMock fill={`url(#dm-${index})`} />}
        {type === "onboard" && <OnboardMock fill={`url(#dm-${index})`} />}
        {type === "secure" && <SecureMock fill={`url(#dm-${index})`} />}
      </svg>
    </div>
  );
}

function ChartMock({ fill }) {
  const teeth = Array.from({ length: 16 });
  return (
    <g>
      <rect x="14" y="62" width="612" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      <text x="36" y="92" className="fill-foreground/70" style={{ fontSize: 12, fontFamily: "monospace" }}>
        Tooth chart · #14–#19 selected
      </text>
      {teeth.map((_, i) => {
        const x = 36 + (i % 8) * 72;
        const y = i < 8 ? 120 : 220;
        const active = i === 3 || i === 4 || i === 11;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width="58"
              height="72"
              rx="12"
              fill={active ? fill : undefined}
              className={active ? "" : "fill-foreground/10"}
              opacity={active ? 0.9 : 1}
            />
            <rect
              x={x + 14}
              y={y + 18}
              width="30"
              height="36"
              rx="8"
              className={active ? "fill-white/80" : "fill-foreground/25"}
            />
            <rect x={x + 10} y={y + 58} width="38" height="6" rx="3" className="fill-foreground/20" />
          </g>
        );
      })}
      <rect x="36" y="330" width="120" height="28" rx="14" fill={fill} />
      <rect x="168" y="336" width="90" height="16" rx="8" className="fill-foreground/20" />
      <rect x="272" y="336" width="70" height="16" rx="8" className="fill-foreground/15" />
    </g>
  );
}

function SlipMock({ fill }) {
  return (
    <g>
      <rect x="14" y="62" width="380" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      <rect x="408" y="62" width="218" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      <rect x="36" y="84" width="140" height="10" rx="5" className="fill-foreground/55" />
      <rect x="36" y="106" width="90" height="8" rx="4" className="fill-foreground/25" />
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(36, ${140 + i * 70})`}>
          <rect width="336" height="56" rx="12" className="fill-black/[0.03] dark:fill-white/[0.06]" />
          <rect x="14" y="14" width="110" height="8" rx="4" className="fill-foreground/50" />
          <rect x="14" y="32" width="180" height="8" rx="4" className="fill-foreground/25" />
          <rect x="260" y="16" width="56" height="24" rx="12" fill={i === 0 ? fill : undefined} className={i === 0 ? "" : "fill-foreground/15"} />
        </g>
      ))}
      <rect x="430" y="84" width="100" height="10" rx="5" className="fill-foreground/50" />
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x="430" y={120 + i * 52} width="174" height="40" rx="10" className="fill-foreground/10" />
      ))}
      <rect x="430" y="340" width="174" height="28" rx="14" fill={fill} />
    </g>
  );
}

function LabsMock({ fill }) {
  return (
    <g>
      <rect x="14" y="62" width="612" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      <rect x="36" y="88" width="190" height="10" rx="5" className="fill-foreground/50" />
      <rect x="36" y="110" width="130" height="8" rx="4" className="fill-foreground/25" />
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(36, ${140 + i * 70})`}>
          <rect width="568" height="56" rx="12" className="fill-black/[0.03] dark:fill-white/[0.06]" />
          <rect x="14" y="14" width="120" height="8" rx="4" className="fill-foreground/45" />
          <rect x="14" y="30" width="160" height="8" rx="4" className="fill-foreground/22" />
          <rect x="420" y="16" width="132" height="24" rx="12" fill={i === 0 ? fill : undefined} className={i === 0 ? "" : "fill-foreground/12"} />
        </g>
      ))}
    </g>
  );
}

function ListMock({ fill }) {
  const rows = [
    { status: "In production", due: "Thu" },
    { status: "Ready", due: "Today" },
    { status: "Submitted", due: "Fri" },
    { status: "Shipped", due: "Wed" },
    { status: "QC", due: "Mon" },
  ];
  return (
    <g>
      <rect x="14" y="62" width="612" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      <rect x="36" y="84" width="200" height="28" rx="14" className="fill-foreground/10" />
      <rect x="250" y="88" width="80" height="20" rx="10" fill={fill} />
      <rect x="344" y="88" width="70" height="20" rx="10" className="fill-foreground/15" />
      {rows.map((r, i) => (
        <g key={r.status} transform={`translate(36, ${132 + i * 46})`}>
          <rect width="568" height="38" rx="10" className="fill-black/[0.03] dark:fill-white/[0.05]" />
          <circle cx="18" cy="19" r="5" fill={i === 1 ? fill : undefined} className={i === 1 ? "" : "fill-foreground/25"} />
          <rect x="36" y="12" width={140 - i * 8} height="8" rx="4" className="fill-foreground/50" />
          <rect x="220" y="12" width="90" height="8" rx="4" className="fill-foreground/30" />
          <rect x="360" y="8" width="100" height="22" rx="11" className="fill-foreground/10" />
          <rect x="490" y="12" width="50" height="8" rx="4" className="fill-foreground/35" />
        </g>
      ))}
    </g>
  );
}

function NotesMock({ fill }) {
  return (
    <g>
      <rect x="14" y="62" width="612" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      <rect x="36" y="84" width="160" height="10" rx="5" className="fill-foreground/55" />
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(${i % 2 === 0 ? 36 : 180}, ${120 + i * 70})`}>
          <rect width="280" height="56" rx="14" fill={i === 1 ? fill : undefined} className={i === 1 ? "" : "fill-black/[0.04] dark:fill-white/[0.06]"} opacity={i === 1 ? 0.85 : 1} />
          <rect x="16" y="16" width={140 - i * 20} height="8" rx="4" className={i === 1 ? "fill-white/80" : "fill-foreground/45"} />
          <rect x="16" y="32" width={100 - i * 10} height="8" rx="4" className={i === 1 ? "fill-white/50" : "fill-foreground/25"} />
        </g>
      ))}
      <rect x="400" y="120" width="200" height="220" rx="16" className="fill-black/[0.03] dark:fill-white/[0.05]" />
      <rect x="420" y="144" width="120" height="8" rx="4" className="fill-foreground/45" />
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x="420" y={172 + i * 36} width={160 - i * 18} height="10" rx="5" className="fill-foreground/20" />
      ))}
    </g>
  );
}

function StudioMock({ fill }) {
  return (
    <g>
      <rect x="14" y="62" width="612" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      <rect x="36" y="84" width="200" height="10" rx="5" className="fill-foreground/50" />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <rect
          key={i}
          x={36 + (i % 3) * 190}
          y={120 + Math.floor(i / 3) * 110}
          width="172"
          height="92"
          rx="14"
          fill={i === 1 ? fill : undefined}
          className={i === 1 ? "" : "fill-black/[0.03] dark:fill-white/[0.06]"}
          opacity={i === 1 ? 0.9 : 1}
        />
      ))}
      <rect x="36" y="346" width="130" height="24" rx="12" className="fill-foreground/15" />
      <rect x="176" y="346" width="120" height="24" rx="12" fill={fill} />
      <rect x="306" y="346" width="100" height="24" rx="12" className="fill-foreground/12" />
    </g>
  );
}

function PracticeMock({ fill }) {
  return (
    <g>
      <rect x="14" y="62" width="200" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      <rect x="230" y="62" width="396" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      {["Main St", "Westside", "North"].map((label, i) => (
        <g key={label} transform={`translate(36, ${90 + i * 52})`}>
          <rect width="156" height="40" rx="12" fill={i === 0 ? fill : undefined} className={i === 0 ? "" : "fill-foreground/10"} opacity={i === 0 ? 0.9 : 1} />
          <rect x="14" y="16" width={70 + i * 10} height="8" rx="4" className={i === 0 ? "fill-white/80" : "fill-foreground/40"} />
        </g>
      ))}
      <rect x="254" y="88" width="140" height="10" rx="5" className="fill-foreground/55" />
      <rect x="254" y="116" width="100" height="8" rx="4" className="fill-foreground/25" />
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={254 + (i % 2) * 170} y={150 + Math.floor(i / 2) * 100} width="150" height="80" rx="14" className="fill-black/[0.03] dark:fill-white/[0.06]" />
      ))}
    </g>
  );
}

function OnboardMock({ fill }) {
  return (
    <g>
      <rect x="14" y="62" width="612" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      {[0, 1, 2, 3].map((i) => (
        <g key={i} transform={`translate(48, ${100 + i * 64})`}>
          <circle cx="14" cy="14" r="14" fill={i < 3 ? fill : undefined} className={i < 3 ? "" : "fill-foreground/15"} />
          {i < 3 && <path d="M8 14 l4 4 8-8" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" />}
          <rect x="44" y="6" width={180 - i * 20} height="10" rx="5" className="fill-foreground/45" />
          <rect x="44" y="22" width={240 - i * 30} height="8" rx="4" className="fill-foreground/20" />
          {i < 3 && <line x1="14" y1="28" x2="14" y2="64" className="stroke-foreground/15" strokeWidth="2" />}
        </g>
      ))}
      <rect x="420" y="120" width="170" height="200" rx="16" className="fill-black/[0.03] dark:fill-white/[0.05]" />
      <rect x="440" y="148" width="130" height="10" rx="5" className="fill-foreground/40" />
      <rect x="440" y="180" width="130" height="28" rx="14" fill={fill} />
      <rect x="440" y="224" width="130" height="28" rx="14" className="fill-foreground/15" />
      <rect x="440" y="268" width="130" height="28" rx="14" className="fill-foreground/10" />
    </g>
  );
}

function SecureMock({ fill }) {
  return (
    <g>
      <rect x="14" y="62" width="612" height="324" rx="14" className="fill-black/[0.02] dark:fill-white/[0.04]" />
      <rect x="200" y="110" width="240" height="180" rx="24" className="fill-black/[0.04] dark:fill-white/[0.06]" />
      <rect x="270" y="150" width="100" height="80" rx="16" fill={fill} opacity="0.9" />
      <path
        d="M300 175 v18 a20 20 0 0 0 40 0 v-18"
        fill="none"
        stroke="white"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <rect x="290" y="188" width="60" height="28" rx="8" className="fill-white/80" />
      <rect x="56" y="320" width="160" height="28" rx="14" className="fill-foreground/15" />
      <rect x="240" y="320" width="160" height="28" rx="14" fill={fill} />
      <rect x="424" y="320" width="160" height="28" rx="14" className="fill-foreground/15" />
    </g>
  );
}
